/* DON'T EDIT THIS FILE: edit original and run build again */ /* eslint-disable max-lines */
// FIXME: just to not make a mess of changed imports, this will be divided into files in another branch (future@replace-nosis)

import {
  ArgentineIndecRegionCodes,
  ArgentineProvinceCode,
} from "../../credit/form/argentina-provinces.ts";
import { Gender } from "../../credit/gender/genders.ts";
import { FormattedCuit } from "../../credit/gov-id-number/cuit.ts";
import { FormattedDni } from "../../credit/gov-id-number/format-dni.ts";
import {
  NosisChecksDetail,
  NosisCurrentDebtByEntityDetail,
  NosisDebtHistoryDetail,
  NosisRejectedChecksDetail,
  NosisRelatedCompany,
  NosisSocialSecurityDebtValue,
} from "../../credit/nosis-types/types.ts";
import {
  GeoLocation,
  StreetAddress,
} from "../../credit/street-address-type/street-address-type.ts";
import { AttachmentRef } from "../../framework/attachment-type/attachment-ref-type.ts";
import { AttachmentItemMap } from "../../framework/attachment-ui/attachment-item.ts";
import { InvestmentProfileMap } from "../../onboarding/onboarding-section/investment-profile-type/investment-profile-type.ts";
import { AuneAgentLink } from "./aune-agent-link.ts";
import { AunePosition } from "./aune-position.ts";
import { DocumentationMap } from "./documentation.ts";
import { FundsMediumMap } from "./funds-medium-type.ts";
import { FundsOfOriginMap } from "./funds-origin-type.ts";
import { RegistryInscriptionType } from "./inscription-type.ts";
import { IsUsPerson } from "./is-us-person.ts";
import { MaritalStatus } from "./marital-status.ts";
import { PoliticalBond } from "./political-bond-type.ts";
import {
  FullPepOptions,
  SimplePepOptions,
} from "./politically-exposed-person-type.ts";
import { VatCondition } from "./vat-condition.ts";

//
// LineInfo
//

export const entityFieldEquity = "equity";
export const entityFieldNetResult = "netResult";
export const entityFieldLeverage = "leverage";
export const entityFieldRequestedAmount = "requestedAmount";
export const entityFieldHasRequestedAmount = "hasRequestedAmount";
export const entityFieldThreePercentOfSales = "threePercentOfSales";
export const entityFieldAnalysisInputDate = "analysisInputDate";

export type InlineEntityLineInfo = {
  [entityFieldEquity]?: number;
  [entityFieldNetResult]?: number;
  [entityFieldLeverage]?: number | null;
  [entityFieldRequestedAmount]?: number;
  [entityFieldHasRequestedAmount]?: boolean;
  [entityFieldThreePercentOfSales]?: number;
  [entityFieldAnalysisInputDate]?: string;
};

export const inlineEntityLineInfoVariables: Record<
  keyof InlineEntityLineInfo,
  true
> = {
  [entityFieldEquity]: true,
  [entityFieldNetResult]: true,
  [entityFieldLeverage]: true,
  [entityFieldRequestedAmount]: true,
  [entityFieldHasRequestedAmount]: true,
  [entityFieldThreePercentOfSales]: true,
  [entityFieldAnalysisInputDate]: true,
};

//
// BankDebtAndRejectedChecksInfo
//

export const entityFieldBcraAntiquity = "bcraAntiquity";
export const entityFieldWorstBcraDebtSituation = "worstBcraDebtSituation";
export const entityFieldCurrentDebtByEntityDetail = "currentDebtByEntityDetail";
export const entityFieldDebtHistoryLast24mDetail = "debtHistoryDetail";
export const entityFieldRejectedChecksLast30Checks =
  "rejectedChecksLast30Checks";
export const entityFieldRejectedChecksCompanyLast6mDetail =
  "rejectedChecksCompanyLast6mDetail";
export const entityFieldRejectedChecksCompany7to12mDetail =
  "rejectedChecksCompany7to12mDetail";
export const entityFieldRejectedChecksCompany13to24mDetail =
  "rejectedChecksCompany13to24mDetail";
export const entityFieldRejectedChecksHumanLast6mDetail =
  "rejectedChecksHumanLast6mDetail";
export const entityFieldRejectedChecksHuman7to12mDetail =
  "rejectedChecksHuman7to12mDetail";
export const entityFieldRejectedChecksHuman13to24mDetail =
  "rejectedChecksHuman13to24mDetail";

// derived
export const derivedEntityFieldUnpaidChecksAmountLast24Months =
  "unpaidChecksAmountLast24Months";
export const derivedEntityFieldPaidChecksAmountLast24Months =
  "paidChecksAmountLast24Months";
export const derivedEntityFieldUnpaidChecksFineLast30Checks =
  "unpaidChecksFineLast30Checks";
export const derivedEntityFieldSit2orAboveCurrentDebtAmount =
  "sit2orAboveCurrentDebtAmount";
export const derivedEntityFieldSit2CurrentDebtAmount = "sit2CurrentDebtAmount";
export const derivedEntityFieldSit3CurrentDebtAmount = "sit3CurrentDebtAmount";
export const derivedEntityFieldSit4orAboveCurrentDebtAmount =
  "sit4orAboveCurrentDebtAmount";
export const derivedEntityFieldCurrentDebtTotal = "currentDebtTotal";
export const derivedEntityFieldCurrentDebt10PercentOverTotal =
  "currentDebt10PercentOverTotal";
export const derivedEntityFieldRejectedChecksDetailLast24months =
  "rejectedChecksDetailLast24months";
export const derivedEntityFieldRejectedChecksAmountLast12Months =
  "rejectedChecksAmountLast12Months";
export const derivedEntityFieldRejectedChecksAmountLast6Months =
  "rejectedChecksAmountLast6Months";

export type InlineEntityRejectedChecksDetail = {
  count: number;
  paidCount: number;
  unpaidCount: number;
  unpaidAmount: number;
  paidAmount: number;
};

export type InlineEntityBankDebtAndRejectedChecksInfoDerivedVariables = {
  [derivedEntityFieldUnpaidChecksAmountLast24Months]?: number;
  [derivedEntityFieldPaidChecksAmountLast24Months]?: number;
  [derivedEntityFieldUnpaidChecksFineLast30Checks]?: number;
  [derivedEntityFieldSit2orAboveCurrentDebtAmount]?: number;
  [derivedEntityFieldSit2CurrentDebtAmount]?: number;
  [derivedEntityFieldSit3CurrentDebtAmount]?: number;
  [derivedEntityFieldSit4orAboveCurrentDebtAmount]?: number;
  [derivedEntityFieldCurrentDebtTotal]?: number;
  [derivedEntityFieldCurrentDebt10PercentOverTotal]?: number;
  [derivedEntityFieldRejectedChecksDetailLast24months]?: InlineEntityRejectedChecksDetail;
  [derivedEntityFieldRejectedChecksAmountLast12Months]?: number;
  [derivedEntityFieldRejectedChecksAmountLast6Months]?: number;
};

export type InlineEntityBankDebtAndRejectedChecksInfoBaseVariables = {
  [entityFieldBcraAntiquity]?: number;
  [entityFieldWorstBcraDebtSituation]?: string;
  [entityFieldCurrentDebtByEntityDetail]?: NosisCurrentDebtByEntityDetail;
  [entityFieldDebtHistoryLast24mDetail]?: NosisDebtHistoryDetail;
  [entityFieldRejectedChecksLast30Checks]?: NosisRejectedChecksDetail[];
  [entityFieldRejectedChecksCompanyLast6mDetail]?: NosisChecksDetail[];
  [entityFieldRejectedChecksCompany7to12mDetail]?: NosisChecksDetail[];
  [entityFieldRejectedChecksCompany13to24mDetail]?: NosisChecksDetail[];
  [entityFieldRejectedChecksHumanLast6mDetail]?: NosisChecksDetail[];
  [entityFieldRejectedChecksHuman7to12mDetail]?: NosisChecksDetail[];
  [entityFieldRejectedChecksHuman13to24mDetail]?: NosisChecksDetail[];
};

export type InlineEntityBankDebtAndRejectedChecksInfo =
  InlineEntityBankDebtAndRejectedChecksInfoBaseVariables &
    InlineEntityBankDebtAndRejectedChecksInfoDerivedVariables;

export const inlineEntityBankDebtAndRejectedChecksInfoBaseVariables: Record<
  keyof InlineEntityBankDebtAndRejectedChecksInfoBaseVariables,
  true
> = {
  [entityFieldBcraAntiquity]: true,
  [entityFieldWorstBcraDebtSituation]: true,
  [entityFieldCurrentDebtByEntityDetail]: true,
  [entityFieldDebtHistoryLast24mDetail]: true,
  [entityFieldRejectedChecksLast30Checks]: true,
  [entityFieldRejectedChecksCompanyLast6mDetail]: true,
  [entityFieldRejectedChecksCompany7to12mDetail]: true,
  [entityFieldRejectedChecksCompany13to24mDetail]: true,
  [entityFieldRejectedChecksHumanLast6mDetail]: true,
  [entityFieldRejectedChecksHuman7to12mDetail]: true,
  [entityFieldRejectedChecksHuman13to24mDetail]: true,
};

export const inlineEntityBankDebtAndRejectedChecksInfoDerivedVariables: Record<
  keyof InlineEntityBankDebtAndRejectedChecksInfoDerivedVariables,
  true
> = {
  [derivedEntityFieldUnpaidChecksAmountLast24Months]: true,
  [derivedEntityFieldPaidChecksAmountLast24Months]: true,
  [derivedEntityFieldUnpaidChecksFineLast30Checks]: true,
  [derivedEntityFieldSit2orAboveCurrentDebtAmount]: true,
  [derivedEntityFieldSit2CurrentDebtAmount]: true,
  [derivedEntityFieldSit3CurrentDebtAmount]: true,
  [derivedEntityFieldSit4orAboveCurrentDebtAmount]: true,
  [derivedEntityFieldCurrentDebtTotal]: true,
  [derivedEntityFieldCurrentDebt10PercentOverTotal]: true,
  [derivedEntityFieldRejectedChecksDetailLast24months]: true,
  [derivedEntityFieldRejectedChecksAmountLast12Months]: true,
  [derivedEntityFieldRejectedChecksAmountLast6Months]: true,
};

//
// compliance info
//

export const entityFieldIsPoliticallyExposedPerson =
  "isPoliticallyExposedPerson";

export type InlineEntityComplianceInfo = {
  [entityFieldIsPoliticallyExposedPerson]?: boolean | null;
};

export const inlineEntityComplianceInfoVariables: Record<
  keyof InlineEntityComplianceInfo,
  true
> = {
  [entityFieldIsPoliticallyExposedPerson]: true,
};

//
// contact info
//

export const entityFieldPhone = "phoneNumber";

export type InlineEntityContactInfo = {
  [entityFieldPhone]?: string;
};

export const inlineEntityContactInfoVariables: Record<
  keyof InlineEntityContactInfo,
  true
> = {
  [entityFieldPhone]: true,
};

//
// contests and bankrupcies info
//

export const entityFieldContestsAndBankrupciesLast60m =
  "contestsAndBankrupciesLast60m";

export type InlineEntityContestsAndBankruptciesInfo = {
  [entityFieldContestsAndBankrupciesLast60m]?: number;
};

export const inlineEntityContestsAndBankruptciesInfoVariables: Record<
  keyof InlineEntityContestsAndBankruptciesInfo,
  true
> = {
  [entityFieldContestsAndBankrupciesLast60m]: true,
};

//
// nosis info
//

export const entityFieldNosisScore = "nosisScore";

export type InlineEntityNosisInfo = {
  [entityFieldNosisScore]?: number;
};

export const inlineEntityNosisInfoVariables: Record<
  keyof InlineEntityNosisInfo,
  true
> = {
  [entityFieldNosisScore]: true,
};

//
// related entities info
//

export const entityFieldRelatedEntities = "relatedEntities";

export type InlineEntityRelatedEntitiesInfo = {
  [entityFieldRelatedEntities]?: NosisRelatedCompany[];
};

export const inlineEntityRelatedEntitiesInfoVariables: Record<
  keyof InlineEntityRelatedEntitiesInfo,
  true
> = {
  [entityFieldRelatedEntities]: true,
};

//
// simple cuit info
//

export const entityFieldCuit = "cuit";
export const entityFieldDni = "dni";
export const entityFieldGender = "gender";
export const entityFieldEntityName = "entityName";
export const entityFieldFirstName = "firstName";
export const entityFieldLastName = "lastName";
export const entityFieldDateOfBirth = "dateOfBirth";
export const entityFieldPlaceOfBirth = "placeOfBirth";
export const entityFieldDeceasedDate = "deceasedDate";
export const entityFieldFiscalStreetAddress = "fiscalStreetAddress";
export const entityFieldBusinessEntityType = "businessEntityType";
export const entityFieldBusinessStartDate = "businessStartDate";

// derived
export const entityFieldEntityShortName = "entityShortName";
export const entityFieldCountry = "country";
export const entityFieldNationality = "nationality";
export const entityFieldCountryOfResidence = "countryOfResidence";
export const derivedEntityFieldAge = "age";
export const derivedEntityFieldCompanyAge = "companyAge";
export const derivedEntityFieldIsHuman = "isHuman";
export const derivedEntityFieldIsCompany = "isCompany";
export const derivedEntityFieldArgentineRegion = "argentinaRegion";
export const derivedEntityFieldIsDeceased = "isDeceased";

export type InlineEntitySimpleCuitInfoBaseVariables = {
  [entityFieldCuit]?: FormattedCuit | "";
  [entityFieldDni]?: FormattedDni;
  [entityFieldGender]?: Gender;
  [entityFieldEntityName]?: string;
  [entityFieldFirstName]?: string;
  [entityFieldLastName]?: string;
  [entityFieldDateOfBirth]?: string;
  [entityFieldFiscalStreetAddress]?: StreetAddress;
  [entityFieldBusinessEntityType]?: string;
  [entityFieldBusinessStartDate]?: string;
  [entityFieldDeceasedDate]?: string;
};

export type InlineEntitySimpleCuitInfoDerivedVariables = {
  [entityFieldEntityShortName]?: string;
  [entityFieldPlaceOfBirth]?: GeoLocation;
  [entityFieldCountry]?: string;
  [entityFieldNationality]?: string;
  [entityFieldCountryOfResidence]?: string;
  [derivedEntityFieldAge]?: number;
  [derivedEntityFieldCompanyAge]?: number;
  [derivedEntityFieldIsCompany]?: boolean;
  [derivedEntityFieldIsHuman]?: boolean;
  [derivedEntityFieldArgentineRegion]?: ArgentineIndecRegionCodes;
  [derivedEntityFieldIsDeceased]?: boolean;
};

export type InlineEntitySimpleCuitInfo =
  InlineEntitySimpleCuitInfoBaseVariables &
    InlineEntitySimpleCuitInfoDerivedVariables;

export const inlineEntitySimpleCuitInfoBaseVariables: Record<
  keyof InlineEntitySimpleCuitInfoBaseVariables,
  true
> = {
  [entityFieldCuit]: true,
  [entityFieldDni]: true,
  [entityFieldGender]: true,
  [entityFieldEntityName]: true,
  [entityFieldFirstName]: true,
  [entityFieldLastName]: true,
  [entityFieldDateOfBirth]: true,
  [entityFieldFiscalStreetAddress]: true,
  [entityFieldBusinessEntityType]: true,
  [entityFieldBusinessStartDate]: true,
  [entityFieldDeceasedDate]: true,
};

export const inlineEntitySimpleCuitInfoDerivedVariables: Record<
  keyof InlineEntitySimpleCuitInfoDerivedVariables,
  true
> = {
  [entityFieldEntityShortName]: true,
  [entityFieldPlaceOfBirth]: true,
  [entityFieldCountry]: true,
  [entityFieldNationality]: true,
  [entityFieldCountryOfResidence]: true,
  [derivedEntityFieldAge]: true,
  [derivedEntityFieldCompanyAge]: true,
  [derivedEntityFieldIsCompany]: true,
  [derivedEntityFieldIsHuman]: true,
  [derivedEntityFieldArgentineRegion]: true,
  [derivedEntityFieldIsDeceased]: true,
};

// social security info (anses)
export const entityFieldEmployeeCount = "employeeCount";
export const entityFieldSocialSecurityDebtLast24mDetail = "socialSecurityDebt";
export const entityFieldAfipInscriptionAntiquity = "afipInscriptionAntiquity";
// derived
export const derivedEntityFieldHasSocialSecurityDebtOlderTo6Months =
  "hasSocialSecurityDebtOlderTo6Months";
export const derivedEntityFieldHasSocialSecurityDebtOlderTo3Months =
  "hasSocialSecurityDebtOlderTo3Months";

export type InlineEntitySocialSecurityInfoDerivedVariables = {
  // FIXME?: this info is repeated (future@replace-nosis)
  [derivedEntityFieldHasSocialSecurityDebtOlderTo6Months]?: boolean;
  [derivedEntityFieldHasSocialSecurityDebtOlderTo3Months]?: boolean;
};

export type InlineEntitySocialSecurityInfoBaseVariables = {
  // nosis:VI_Empleados_Cant (fuente: anses)
  [entityFieldEmployeeCount]?: number;
  // nosis:AP_24m_Empleador_Detalle (fuente: anses?)
  [entityFieldSocialSecurityDebtLast24mDetail]?: NosisSocialSecurityDebtValue;
  [entityFieldAfipInscriptionAntiquity]?: number;
};

export type InlineEntitySocialSecurityInfo =
  InlineEntitySocialSecurityInfoBaseVariables &
    InlineEntitySocialSecurityInfoDerivedVariables;

export const inlineEntitySocialSecurityInfoBaseVariables: Record<
  keyof InlineEntitySocialSecurityInfoBaseVariables,
  true
> = {
  [entityFieldEmployeeCount]: true,
  [entityFieldSocialSecurityDebtLast24mDetail]: true,
  [entityFieldAfipInscriptionAntiquity]: true,
};

export const inlineEntitySocialSecurityInfoDerivedVariables: Record<
  keyof InlineEntitySocialSecurityInfoDerivedVariables,
  true
> = {
  [derivedEntityFieldHasSocialSecurityDebtOlderTo6Months]: true,
  [derivedEntityFieldHasSocialSecurityDebtOlderTo3Months]: true,
};

// tax info (afip)
export const entityFieldMainActivity = "mainActivity"; // repeat?
export const entityFieldAfipActivity = "afipActivity";
export const entityFieldAfipActivityCode = "afipActivityCode";
export const entityFieldSecondaryActivity = "secondaryActivity";
export const entityFieldAfipSecondaryActivityCode = "afipSecondaryActivityCode";
export const entityFieldAfipTertiaryActivityCode = "afipTertiaryActivityCode";
export const entityFieldAfipQuaternaryActivityCode =
  "afipQuaternaryActivityCode";
export const entityFieldBusinessSector = "businessSector";

export type InlineEntityTaxInfoBaseVariables = {
  [entityFieldAfipActivityCode]?: number;
  [entityFieldAfipSecondaryActivityCode]?: number;
  [entityFieldAfipTertiaryActivityCode]?: number;
  [entityFieldAfipQuaternaryActivityCode]?: number;
};

export type InlineEntityTaxInfoDerivedVariables = {
  [entityFieldMainActivity]?: string;
  [entityFieldAfipActivity]?: string;
  [entityFieldSecondaryActivity]?: string;
  [entityFieldBusinessSector]?: string;
};

export type InlineEntityTaxInfo = InlineEntityTaxInfoBaseVariables &
  InlineEntityTaxInfoDerivedVariables;

export const inlineEntityTaxInfoBaseVariables: Record<
  keyof InlineEntityTaxInfoBaseVariables,
  true
> = {
  [entityFieldAfipActivityCode]: true,
  [entityFieldAfipSecondaryActivityCode]: true,
  [entityFieldAfipTertiaryActivityCode]: true,
  [entityFieldAfipQuaternaryActivityCode]: true,
};

export const inlineEntityTaxInfoDerivedVariables: Record<
  keyof InlineEntityTaxInfoDerivedVariables,
  true
> = {
  [entityFieldMainActivity]: true,
  [entityFieldAfipActivity]: true,
  [entityFieldSecondaryActivity]: true,
  [entityFieldBusinessSector]: true,
};

// pyme certificate info
export const entityFieldPymeCertificateSector = "pymeCertificateSector";
export const entityFieldPymeCertificateCategory = "pymeCertificateCategory";
export const entityFieldPymeCertificateDate = "pymeCertificateDate";
export const entityFieldIsSme = "isSme";

export type InlineEntityPymeCertificateInfo = {
  // lufe.entity.pymeCertificate.sector
  [entityFieldPymeCertificateSector]?: string;
  // lufe.entity.pymeCertificate.category
  [entityFieldPymeCertificateCategory]?: string;
  [entityFieldPymeCertificateDate]?: string;
  [entityFieldIsSme]?: boolean;
};

export const inlineEntityPymeCertificateInfoVariables: Record<
  keyof InlineEntityPymeCertificateInfo,
  true
> = {
  // MF_Lufe_PymeCert_Sector
  [entityFieldPymeCertificateSector]: true,
  // MF_Lufe_PymeCert_Size
  [entityFieldPymeCertificateCategory]: true,
  // MF_Lufe_PymeCert_Date
  [entityFieldPymeCertificateDate]: true,
  [entityFieldIsSme]: true,
};

//
// raw nosis info (FIXME: remove when variables are reviewed) (future@replace-nosis)
//

export const inlineEntityRawNosisDetail = "rawNosisDetail";

export type InlineEntityRawNosisInfo = {
  [inlineEntityRawNosisDetail]?: any;
};

export const inlineEntityRawNosisInfoVariables: Record<
  keyof InlineEntityRawNosisInfo,
  true
> = {
  [inlineEntityRawNosisDetail]: true,
};

// uncategorized

export const entityFieldMainEmail = "email";
export const entityFieldSecondaryEmail = "secondaryEmail";
export const entityFieldSecondaryPhone = "secondaryPhoneNumber";
export const entityFieldFax = "fax";
export const entityFieldFirstFiscalStreetAddress = "firstFiscalStreetAddress"; // repeat with entityFieldFiscalStreetAddress
export const entityFieldSecondFiscalStreetAddress = "secondFiscalStreetAddress";
export const entityFieldAfipEarningsDocument = "afipEarningsDocument";
export const entityFieldForeignIdNumber = "foreignIdNumber";
export const entityFieldPlaceOfIncorporation = "placeOfIncorporation";
export const entityFieldDateOfIncorporation = "dateOfIncorporation";
export const entityFieldCommercialName = "commercialName";
export const entityFieldRevenueVolumeCurrency = "revenueVolumeCurrency";
export const entityFieldRevenueVolumeAmount = "revenueVolumeAmount";
export const entityFieldIsRequiredToInform = "isRequiredToInform";
export const entityFieldIsUsPerson = "isUsPerson";
export const entityFieldOpenMatbaRofexAccount = "openMatbaRofexAccount";
export const entityFieldAuthorizeSrgCommissionsAndExpenses =
  "authorizeSrgCommissionsAndExpenses";
export const entityFieldStatuteDate = "statuteDate";
export const entityFieldRegistryInscriptionDate = "registryInscriptionDate";
export const entityFieldRegistryNumber = "registryNumber";
export const entityFieldRegistryInscriptionType = "registryInscriptionType";
export const entityFieldMaritalStatus = "maritalStatus";
export const entityFieldAlreadyAvailable = "documentationAlreadyAvailable";
export const entityFieldIsInsuranceCompany = "isInsuranceCompany";
export const entityFieldCurrentStatutoryLaw = "currentStatutoryLaw";
export const entityFieldStatute = "statute";
export const entityFieldBalanceSheets = "balanceSheets";
export const entityFieldFundsOrigin = "fundsOrigin";
export const entityFieldFundsMedium = "fundsMedium";
export const entityFieldShareholderMinutes = "shareholderMinutes";
export const entityFieldBoardMinutes = "boardMinutes";
export const entityFieldSrlShareholderMinutes = "srlShareholderMinutes";
export const entityFieldSrlBoardMinutes = "srlBoardMinutes";
export const entityFieldFinancialStatementsApprovalDocument =
  "financialStatementsApprovalDocument";
export const entityFieldAuthoritiesDesignationDocument =
  "authoritiesDesignationDocument";
export const entityFieldFinancialTrustType = "financialTrustType";
export const entityFieldTrustStatute = "trustStatute";
export const entityFieldTrustLastCertifiedBalanceSheet =
  "trustLastCertifiedBalanceSheet";
export const entityFieldTrustShareholderMinutes = "trustShareholderMinutes";
export const entityFieldTrustBoardMinutes = "trustBoardMinutes";
export const entityFieldGrantors = "grantors";
export const entityFieldAssemblyAttendanceDocument =
  "assemblyAttendanceDocument";
export const entityFieldSupportingDocumentation = "supportingDocumentation";
export const entityFieldSourceOfFundsDocuments = "sourceOfFundsDocuments";
export const entityFieldProofOfAddressDocument = "proofOfAddressDocument";
export const entityFieldDocumentationAlreadyAvailable =
  "documentationAlreadyAvailable";
export const entityFieldPymeType = "pymeType";
export const entityFieldMipymeCertificates = "mipymeCertificates";
export const entityFieldSalesReports = "salesReports";
export const entityFieldHasNoFinancialDebts = "hasNoFinancialDebts";
export const entityFieldFinancialDebt = "financialDebtDocuments";
export const entityFieldIncomeTaxCondition = "incomeTaxCondition";
export const entityFieldProfession = "profession";
export const entityFieldIncomeTaxStatements = "incomeTaxStatements";
export const entityFieldPropertyStatements = "propertyStatements";
export const entityFieldCashFlowDocuments = "cashFlowDocuments";
export const entityFieldIdFront = "idFront";
export const entityFieldIdBack = "idBack";
export const entityFieldIncomeProof = "incomeProof";
export const entityFieldExtraordinaryIncomeProof = "extraordinaryIncomeProof";
export const entityFieldPosition = "position";
export const entityFieldAunePosition = "aunePosition";
export const entityFieldFiscalId = "fiscalId";
export const entityFieldSecondFiscalId = "secondFiscalId";
export const entityFieldInstitution = "institution";
export const entityFieldIsPoliticallyExposedByBond =
  "isPoliticallyExposedByBond";
export const entityFieldPoliticalPositionUruguay = "politicalPositionUruguay";
export const entityFieldPoliticalBondUruguay = "politicalBondUruguay";
export const entityFieldPoliticallyExposedPersonType =
  "politicallyExposedPersonType";
export const entityFieldPoliticallyExposedPersonDetail =
  "politicallyExposedPersonDetail";
export const entityFieldPepStartDate = "pepStartDate";
export const entityFieldValidPepPosition = "pepValidPosition";
export const entityFieldPepBond = "pepBond";
export const entityFieldPepBondResidence = "pepBondResidence";
export const entityFieldPepBondPosition = "pepBondPosition";
export const entityFieldPepPositionStartDate = "pepPositionStartDate";
export const entityFieldPepPositionEndDate = "pepPositionEndDate";
export const entityFieldIsTraded = "isTraded";
export const entityFieldMarketOfShares = "marketOfShares";
export const entityFieldTradedSharesPercentage = "tradedSharesPercentage";
export const entityFieldNoTaxIdNumber = "noTaxIdNumber";
export const entityFieldTaxIdNumber = "taxIdNumber";
export const entityFieldWhyNoTaxId = "whyNoTaxId";
export const entityFieldWhyNoTaxIdOther = "whyNoTaxIdOther";
export const entityFieldOperatesOnOwn = "operatesOnOwn";
export const entityFieldThirdParties = "thirdParties";
export const entityFieldPowerDesignation = "powerDesignation";
export const entityFieldVatCondition = "vatCondition";
export const entityFieldAuneAgentLink = "auneAgentLink";
export const entityFieldPartnerEntityId = "partnerEntityId";
export const entityFieldInvestmentProfile = "investmentProfile";
export const entityFieldSgrDocumentation = "sgrDocumentation";
export const entityFieldAgriculturalLeasedHectares =
  "agriculturalLeasedHectares"; // MF_Hectareas_Arrendadas_Cant
export const entityFieldAgriculturalLeasedHectaresProvince =
  "agriculturalLeasedHectaresProvince";
export const entityFieldAgriculturalLeasedHectaresLocality =
  "agriculturalLeasedHectaresLocality";
export const entityFieldAgriculturalOwnedHectares = "agriculturalOwnedHectares"; // MF_Hectareas_Propias_Cant
export const entityFieldAgriculturalOwnedHectaresProvince =
  "agriculturalOwnedHectaresProvince";
export const entityFieldAgriculturalOwnedHectaresLocality =
  "agriculturalOwnedHectaresLocality";

// additionally we have "pep" + [Spouse, Parent, ParentSpouse, Child, ChildSpouse, Sibling] + field + [Name, Bond, Position]

export type InlineEntityUncategorizedInfo = {
  // only used when no cuit is present (as cuit number defines either company or person)
  isCompany?: boolean;
  // uncategorized
  [entityFieldForeignIdNumber]?: string;
  [entityFieldIsUsPerson]?: IsUsPerson;
  [entityFieldMainEmail]?: string;
  [entityFieldSecondaryPhone]?: string;
  [entityFieldSecondaryEmail]?: string;
  [entityFieldFirstFiscalStreetAddress]?: string;
  [entityFieldSecondFiscalStreetAddress]?: string;
  [entityFieldFax]?: string;
  [entityFieldPlaceOfIncorporation]?: StreetAddress;
  [entityFieldStatuteDate]?: string;
  [entityFieldRegistryInscriptionDate]?: string;
  [entityFieldRegistryNumber]?: number;
  [entityFieldRegistryInscriptionType]?: RegistryInscriptionType;
  [entityFieldCommercialName]?: string;
  [entityFieldMaritalStatus]?: MaritalStatus;
  [entityFieldIsInsuranceCompany]?: boolean;
  [entityFieldCurrentStatutoryLaw]?: AttachmentItemMap;
  [entityFieldStatute]?: DocumentationMap | AttachmentItemMap;
  [entityFieldBalanceSheets]?: DocumentationMap;
  [entityFieldFundsOrigin]?: FundsOfOriginMap;
  [entityFieldFundsMedium]?: FundsMediumMap;
  [entityFieldShareholderMinutes]?: DocumentationMap;
  [entityFieldBoardMinutes]?: DocumentationMap;
  [entityFieldSrlShareholderMinutes]?: DocumentationMap;
  [entityFieldSrlBoardMinutes]?: DocumentationMap;
  [entityFieldFinancialStatementsApprovalDocument]?: DocumentationMap;
  [entityFieldAuthoritiesDesignationDocument]?: DocumentationMap;
  [entityFieldFinancialTrustType]?: string;
  [entityFieldTrustStatute]?: AttachmentItemMap;
  [entityFieldTrustLastCertifiedBalanceSheet]?: AttachmentItemMap;
  [entityFieldTrustShareholderMinutes]?: DocumentationMap;
  [entityFieldTrustBoardMinutes]?: DocumentationMap;
  [entityFieldAfipEarningsDocument]?: AttachmentItemMap;
  [entityFieldAssemblyAttendanceDocument]?: AttachmentItemMap;
  [entityFieldSupportingDocumentation]?: AttachmentItemMap;
  [entityFieldSourceOfFundsDocuments]?: AttachmentItemMap;
  [entityFieldProofOfAddressDocument]?: AttachmentItemMap;
  [entityFieldDocumentationAlreadyAvailable]?: string;
  [entityFieldPymeType]?: string;
  [entityFieldMipymeCertificates]?: DocumentationMap;
  [entityFieldSalesReports]?: DocumentationMap;
  [entityFieldHasNoFinancialDebts]?: boolean;
  [entityFieldFinancialDebt]?: DocumentationMap;
  [entityFieldRevenueVolumeCurrency]?: string;
  [entityFieldRevenueVolumeAmount]?: number;
  [entityFieldIsRequiredToInform]?: boolean;
  [entityFieldVatCondition]?: VatCondition;
  [entityFieldIncomeTaxCondition]?: VatCondition;
  [entityFieldProfession]?: string;
  [entityFieldIncomeTaxStatements]?: DocumentationMap;
  [entityFieldPropertyStatements]?: DocumentationMap;
  [entityFieldCashFlowDocuments]?: DocumentationMap;
  [entityFieldIdFront]?: AttachmentRef;
  [entityFieldIdBack]?: AttachmentRef;
  [entityFieldIncomeProof]?: AttachmentItemMap;
  anonymized?: boolean;
  [entityFieldExtraordinaryIncomeProof]?: AttachmentItemMap;
  [entityFieldPosition]?: string;
  [entityFieldAunePosition]?: AunePosition;
  [entityFieldFiscalId]?: string;
  [entityFieldSecondFiscalId]?: string;
  [entityFieldInstitution]?: string;
  [entityFieldIsTraded]?: boolean;
  [entityFieldMarketOfShares]?: string;
  [entityFieldTradedSharesPercentage]?: number;
  [entityFieldInvestmentProfile]?: InvestmentProfileMap;
  [entityFieldDateOfIncorporation]?: string;
  [entityFieldOpenMatbaRofexAccount]?: string;
  [entityFieldAuthorizeSrgCommissionsAndExpenses]?: string;
  [entityFieldGrantors]?: {
    [id: string]: {
      entityId: string;
    };
  };
  [entityFieldIsPoliticallyExposedByBond]?: boolean;
  [entityFieldPoliticalPositionUruguay]?: string;
  [entityFieldPoliticalBondUruguay]?: { [K in PoliticalBond]?: true };
  [entityFieldPoliticallyExposedPersonType]?:
    | SimplePepOptions
    | FullPepOptions
    | null;
  [entityFieldPoliticallyExposedPersonDetail]?: string;
  [entityFieldNoTaxIdNumber]?: boolean;
  [entityFieldTaxIdNumber]?: string;
  [entityFieldWhyNoTaxId]?: string;
  [entityFieldWhyNoTaxIdOther]?: string;
  [entityFieldOperatesOnOwn]?: string;
  [entityFieldThirdParties]?: {
    [id: string]: {
      entityId: string;
    };
  };
  [entityFieldPowerDesignation]?: AttachmentItemMap;
  [entityFieldAuneAgentLink]?: AuneAgentLink;
  [entityFieldPartnerEntityId]?: string;
  [entityFieldSgrDocumentation]?: AttachmentItemMap;
  [entityFieldAgriculturalOwnedHectares]?: number;
  [entityFieldAgriculturalOwnedHectaresProvince]?: ArgentineProvinceCode;
  [entityFieldAgriculturalOwnedHectaresLocality]?: string;
  [entityFieldAgriculturalLeasedHectares]?: number;
  [entityFieldAgriculturalLeasedHectaresProvince]?: ArgentineProvinceCode;
  [entityFieldAgriculturalLeasedHectaresLocality]?: string;
};

type InlineEntityBaseInfo = {
  id: string;
};

export type InlineEntity = InlineEntityBaseInfo &
  InlineEntitySimpleCuitInfo &
  InlineEntityContactInfo &
  InlineEntityTaxInfo &
  InlineEntityPymeCertificateInfo &
  InlineEntityBankDebtAndRejectedChecksInfo &
  InlineEntityContestsAndBankruptciesInfo &
  InlineEntitySocialSecurityInfo &
  InlineEntityComplianceInfo &
  InlineEntityNosisInfo &
  InlineEntityRawNosisInfo &
  InlineEntityRelatedEntitiesInfo &
  InlineEntityLineInfo &
  InlineEntityUncategorizedInfo;
